import { cn } from '@/lib/utils';

const FEATURES = [
  'AI conducts structured interviews — no human required in the room',
  'Instant scorecards with skill breakdowns delivered after every session',
  'Proctored sessions with tab-switch detection and fullscreen enforcement',
  'Role-based team access, shared pipelines and candidate dashboards',
];

const STATS = [
  { value: '3×',   label: 'Faster hiring' },
  { value: '98%',  label: 'AI accuracy' },
  { value: '500+', label: 'Companies' },
];

export default function LoginBrandPanel({ className }) {
  return (
    <div className={cn('relative flex flex-col overflow-hidden', className)}>
      <style>{`
        @keyframes bg-breathe {
          0%, 100% { background-position: 0% 0%; }
          50%       { background-position: 100% 100%; }
        }
        @keyframes arc-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes text-glow {
          0%, 100% { text-shadow: 0 0 40px rgba(0,153,153,0); }
          50%       { text-shadow: 0 0 40px rgba(0,153,153,0.35); }
        }
        @keyframes line-in {
          from { transform: scaleX(0); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
        .brand-bg {
          background: linear-gradient(150deg, #001414 0%, #002828 50%, #001c1c 100%);
          background-size: 300% 300%;
          animation: bg-breathe 12s ease-in-out infinite;
        }
        .stat-item:hover .stat-value {
          animation: text-glow 1s ease-in-out forwards;
          color: #4dd3d3;
        }
      `}</style>

      {/* 2-shade animated gradient background */}
      <div className="brand-bg absolute inset-0" />

      {/* Decorative: slow-spinning large arc ring — top right */}
      <div
        className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full"
        style={{
          border: '1px solid rgba(0,153,153,0.12)',
          animation: 'arc-spin 40s linear infinite',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-[18rem] w-[18rem] rounded-full"
        style={{
          border: '1px solid rgba(0,153,153,0.07)',
          animation: 'arc-spin 28s linear infinite reverse',
        }}
        aria-hidden
      />

      {/* Subtle bottom-left glow */}
      <div
        className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(0,153,153,0.12) 0%, transparent 70%)' }}
        aria-hidden
      />

      {/* Top accent line */}
      <div className="absolute inset-x-0 top-0 h-px"
           style={{ background: 'linear-gradient(90deg, transparent, rgba(0,153,153,0.5), transparent)' }} />

      {/* ── Content ── */}
      <div className="relative z-10 flex flex-1 flex-col justify-between px-10 py-12 lg:px-14 lg:py-16">

        {/* Wordmark */}
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg"
                style={{ background: 'rgba(0,153,153,0.25)', outline: '1px solid rgba(0,153,153,0.3)' }}>
            <svg viewBox="0 0 20 20" fill="rgba(0,204,204,0.9)" className="h-3.5 w-3.5">
              <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4z" />
              <path d="M5.5 9.643a.75.75 0 00-1.5 0V10a6 6 0 0011.996.47A.75.75 0 0014.5 10v-.357a.75.75 0 00-1.5 0V10a4.5 4.5 0 01-9 0v-.357z" />
            </svg>
          </span>
          <span className="text-sm font-bold tracking-tight text-white/90">EchoHire</span>
        </div>

        {/* Main content block */}
        <div className="space-y-10">

          {/* Headline */}
          <div>
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em]"
               style={{ color: 'rgba(0,204,204,0.7)' }}>
              AI-Powered Interview Platform
            </p>
            <h1 className="text-[2.1rem] font-extrabold leading-[1.15] tracking-tight text-white">
              Structured interviews.
              <br />
              <span style={{
                background: 'linear-gradient(90deg, #4dd3d3, #1ac7c7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Instant insights.
              </span>
              <br />
              Faster hires.
            </h1>
            <p className="mt-5 max-w-[21rem] text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.42)' }}>
              EchoHire handles everything from scheduling to scorecard — so your
              team focuses on deciding, not interviewing.
            </p>
          </div>

          {/* Feature list — clean, no cards */}
          <div className="space-y-3.5">
            {FEATURES.map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: '#1ac7c7' }}
                />
                <p className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.52)' }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div>
          {/* Animated divider */}
          <div
            className="mb-6 h-px origin-left"
            style={{
              background: 'linear-gradient(90deg, rgba(0,153,153,0.5), transparent)',
              animation: 'line-in 1s ease 0.2s both',
            }}
          />
          {/* <div className="flex items-center gap-8">
            {STATS.map(({ value, label }) => (
              <div key={label} className="stat-item cursor-default">
                <p className="stat-value text-xl font-black text-white transition-colors duration-300">
                  {value}
                </p>
                <p className="mt-0.5 text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.32)' }}>
                  {label}
                </p>
              </div>
            ))}
          </div> */}
        </div>

      </div>
    </div>
  );
}
